// React and hooks

// Utilities and API
import { verifyLogin } from 'util/cache'
import { useDocTitle } from 'util/hooks'
import { showToast } from 'components/toast'
import { useEditQuestionEduqueryMutation, useUpdateLessonQuestionsMutation, useUpdateLessonStatusMutation, useGetLessonAssignedClassQuery, useGetLessonQuery, useHasResultsQuery, useGetClassQuery, useRenameLessonMutation, useDeleteQuestionMutation } from 'util/rtk-api'

// Types

// Components
import { AssignClassOverlay } from 'components/lesson-builder/assign-class-overlay'
import { LaunchLessonOverlay } from 'components/lesson-builder/components/launch-lesson-overlay'
import { LessonBuilderHeader } from 'components/lesson-builder/components/lesson-builder-header'
import { PresentationSlide } from 'components/lesson-builder/presentation-slide'
import { QuestionSlide } from 'components/lesson-builder/question-slide'
import { Sidebar } from 'components/lesson-builder/sidebar'
import { SlideNav } from 'components/lesson-builder/slide-nav'
import { StudentLoginsOverlay } from 'components/lesson-builder/student-logins-overlay'
import { CopyToClassOverlay } from 'components/lesson-builder/copy-to-class-overlay'
import { PrintDraftOverlay } from 'components/lesson-builder/print-draft-overlay'
import { PrintAnswerKeyOverlay } from 'components/lesson-builder/print-answer-key-overlay'
import { PrintWorksheetsOverlay } from 'components/lesson-builder/print-worksheets-overlay'

// Context
import { LessonProvider } from 'contexts/lesson-context'

// Assets
import sideBarIcon from 'img/sidebar.svg'
import React, { useState, useEffect, useCallback, useReducer, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Question, QuestionsByDifficulty, QuestionSlide as QuestionSlideType, Lesson, FillBlankQuestion, MultipleChoiceQuestion, ShortAnswerQuestion } from '@all-for-one/shared-types'

// Styles
import 'css/pages/lesson-builder.css'
import popupStyles from 'css/components/lesson-builder/copy-to-class-overlay.module.css'
import { BlueButton } from 'components/blue-button'

// Additional types for the lesson builder
interface Slide {
  type: 'presentation' | 'question'
  index: number
  id?: string
  learningStage: string
  questionsByDifficulty: QuestionsByDifficulty[]
  allApproved: boolean
  slide?: number  // Original slide number from questionSlides
}

interface LessonState {
  name: string
  lessonPdf: string | null
  numSlides: number
  questionSlides: QuestionSlideType[]  // SOURCE OF TRUTH - never deep copy
  totalUnapprovedQuestions: number
  currentSlide: number
  hideSidebar: boolean
  status: string
  lastEditTimestamp: number | null  // Track when "Done" was clicked on an edit
}

type LessonAction =
  | { type: 'INIT_LESSON'; payload: Lesson }
  | { type: 'APPROVE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }
  | { type: 'DISAPPROVE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }
  | { type: 'APPROVE_ALL_QUESTIONS'; slideIndex: number }
  | { type: 'EDIT_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string; payload: Partial<Question> }
  | { type: 'DELETE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }
  | { type: 'SET_CURRENT_SLIDE'; slideNum: number }
  | { type: 'TOGGLE_SIDEBAR' }

// Utility functions for question handling
const isAssessmentStage = (stage: string): boolean =>
  stage === 'Pre-Assessment' || stage === 'Post-Assessment'

// Type-safe merge function that preserves discriminated union types
const mergeQuestion = (question: Question, payload: Partial<Question>): Question => {
  // Never let an incoming payload overwrite the question's id. Pre/Post twins
  // share a base id but must keep their distinct _Pre-Assessment / _Post-Assessment
  // suffixes for separate tracking. A refine/edit payload carries the edited
  // question's own id, which — when the edit is synced to the matching twin —
  // would otherwise clobber the twin's suffix. Ids are immutable after generation,
  // so pinning to the target's own id is always correct.
  const safePayload: Partial<Question> = { ...payload, id: question.id }

  // If payload has a type and it's different from the current type, we need to handle type conversion
  if (safePayload.type && safePayload.type !== question.type) {
    // This is a type conversion - we'll need to handle this case specifically
    return Object.assign({}, question, safePayload) as Question
  }

  // For same-type updates, we can safely merge
  switch (question.type) {
    case 'fill-blank':
      return Object.assign({}, question, safePayload) as FillBlankQuestion
    case 'multiple-choice':
      return Object.assign({}, question, safePayload) as MultipleChoiceQuestion
    case 'short-answer':
      return Object.assign({}, question, safePayload) as ShortAnswerQuestion
  }
}

// Helper function to extract base ID (remove section suffix for comparison)
const getBaseQuestionId = (id: string): string => {
  return id.replace(/_(Pre-Assessment|Post-Assessment|Check for Understanding|Independent Work)$/, '')
}

// Build slides array from questionSlides for rendering
const buildSlidesArray = (questionSlides: QuestionSlideType[], numSlides: number): Slide[] => {
  return Array(numSlides).fill(null).flatMap((_, i) => [
    { type: 'presentation' as const, index: i, learningStage: '', questionsByDifficulty: [], allApproved: true },
    ...questionSlides
      .filter(q => q.slide === i)
      .map(q => ({
        type: 'question' as const,
        index: i,
        id: crypto.randomUUID(),  // Just for React keys
        learningStage: q.learningStage,
        questionsByDifficulty: q.questionsByDifficulty,
        allApproved: q.allApproved || false,
        slide: q.slide  // Keep reference to original slide number
      }))
  ])
}

// Calculate approved status for a slide
const calculateSlideApproved = (questionsByDifficulty: QuestionsByDifficulty[]): boolean => {
  return questionsByDifficulty.every(
    diffQuestions => diffQuestions.questions.every(q => q.approved)
  )
}

// Calculate total unapproved questions from questionSlides
const calculateLessonUnapprovedFromQuestionSlides = (questionSlides: QuestionSlideType[]): number => {
  // Get unique assessment questions (only count once - take first assessment slide)
  const firstAssessmentSlide = questionSlides.find(qSlide => isAssessmentStage(qSlide.learningStage))
  const assessmentQuestions = firstAssessmentSlide?.questionsByDifficulty || []

  // Get non-assessment questions
  const nonAssessmentQuestions = questionSlides
    .filter(qSlide => !isAssessmentStage(qSlide.learningStage))
    .flatMap(qSlide => qSlide.questionsByDifficulty)

  return [...assessmentQuestions, ...nonAssessmentQuestions]
    .flatMap(difficulty => difficulty.questions)
    .filter(q => !q.approved)
    .length
}

/**
 * Updates a specific question in array of QuestionsByDifficulty objects
 * Used by approval and edit handlers to modify question state.
 *
 * @param questions - Array of questions by difficulty
 * @param difficulty - Target difficulty level to update
 * @param questionIndex - Index of question to update
 * @param updateFn - Function to apply to the target question
 * @returns New questions array with updated question
 */
const updateQuestionsByDifficulty = (
  questionsByDifficulty: QuestionsByDifficulty[],
  difficulty: string,
  questionIndex: number,
  updateFn: (q: Question) => Question
): QuestionsByDifficulty[] => {
  return questionsByDifficulty.map((diffSection: QuestionsByDifficulty) => {
    if (diffSection.difficulty !== difficulty) return diffSection

    return {
      ...diffSection,
      questions: diffSection.questions.map((q: Question, qIndex: number) => {
        if (qIndex !== questionIndex) return q
        return updateFn(q) // Apply update function to target question
      })
    }
  })
}

// Display label helpers (mirrors backend display-labels.ts)
const SECTION_ABBR: Record<string, string> = {
  'Pre-Assessment': 'PRE', 'Post-Assessment': 'POS',
  'Check for Understanding': 'CFU', 'Check-for-Understanding': 'CFU',
  'Independent Work': 'IND', 'Independent-Work': 'IND',
}
const LEVEL_LETTER: Record<string, string> = { 'Emerging': 'E', 'Intermediate': 'I', 'Advanced': 'A' }

const recalculateDisplayLabels = (questionSlides: QuestionSlideType[]): QuestionSlideType[] => {
  return questionSlides.map(slide => ({
    ...slide,
    questionsByDifficulty: slide.questionsByDifficulty.map(diffGroup => ({
      ...diffGroup,
      questions: diffGroup.questions.map((q, i) => ({
        ...q,
        displayLabel: `${slide.slide + 1}.${i + 1}.${SECTION_ABBR[slide.learningStage] ?? slide.learningStage}.${LEVEL_LETTER[diffGroup.difficulty] ?? diffGroup.difficulty}`
      }))
    }))
  }))
}

// Action handlers
const handleInitLesson = (state: LessonState, action: { type: 'INIT_LESSON'; payload: any }): LessonState => {
  // Simply store questionSlides as-is (NO deep copy, NO transformation)
  const questionSlides = action.payload.questionSlides || []

  return {
    ...state,
    name: action.payload.name,
    lessonPdf: action.payload.lessonPdf,
    numSlides: action.payload.numSlides,
    questionSlides: questionSlides,
    status: action.payload.status,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(questionSlides)
    // lastEditTimestamp preserved from ...state spread (don't reset on data refetch)
  }
}

const handleApproveQuestion = (state: LessonState, action: { type: 'APPROVE_QUESTION' | 'DISAPPROVE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }): LessonState => {
  // Build slides array to find which questionSlide corresponds to slideIndex
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]

  if (!targetSlide || targetSlide.type !== 'question') return state

  const approved = action.type === 'APPROVE_QUESTION'
  const isDisapproved = action.type === 'DISAPPROVE_QUESTION'
  const newStatus = isDisapproved && state.status === 'Ready' ? 'Draft' : state.status

  // For assessment slides: update all Pre/Post by matching base ID
  if (isAssessmentStage(targetSlide.learningStage)) {
    // Find target question's base ID
    const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
    const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
    if (!targetQuestion) return state

    const baseId = getBaseQuestionId(targetQuestion.id)

    // Update all assessment questionSlides with matching base ID
    const newQuestionSlides = state.questionSlides.map(qSlide => {
      if (!isAssessmentStage(qSlide.learningStage)) return qSlide

      return {
        ...qSlide,
        questionsByDifficulty: qSlide.questionsByDifficulty.map(diffGroup => {
          if (diffGroup.difficulty !== action.difficulty) return diffGroup

          return {
            ...diffGroup,
            questions: diffGroup.questions.map(q => {
              if (getBaseQuestionId(q.id) === baseId) {
                return { ...q, approved, isDisapproved }
              }
              return q
            })
          }
        }),
        allApproved: calculateSlideApproved(
          qSlide.questionsByDifficulty.map(diffGroup => {
            if (diffGroup.difficulty !== action.difficulty) return diffGroup
            return {
              ...diffGroup,
              questions: diffGroup.questions.map(q => {
                if (getBaseQuestionId(q.id) === baseId) {
                  return { ...q, approved, isDisapproved }
                }
                return q
              })
            }
          })
        )
      }
    })

    return {
      ...state,
      questionSlides: newQuestionSlides,
      totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
      status: newStatus,
      lastEditTimestamp: Date.now()  // Mark that an approval happened
    }
  }

  // For non-assessment: only update the matching questionSlide
  const newQuestionSlides = state.questionSlides.map(qSlide => {
    if (qSlide.learningStage !== targetSlide.learningStage || qSlide.slide !== targetSlide.slide) {
      return qSlide
    }

    const newQuestionsByDifficulty = updateQuestionsByDifficulty(
      qSlide.questionsByDifficulty,
      action.difficulty,
      action.questionIndex,
      q => ({ ...q, approved, isDisapproved })
    )

    return {
      ...qSlide,
      questionsByDifficulty: newQuestionsByDifficulty,
      allApproved: calculateSlideApproved(newQuestionsByDifficulty)
    }
  })

  return {
    ...state,
    questionSlides: newQuestionSlides,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
    status: newStatus,
    lastEditTimestamp: Date.now()  // Mark that an edit just happened
  }
}

const handleApproveAllQuestions = (state: LessonState, action: { type: 'APPROVE_ALL_QUESTIONS'; slideIndex: number }): LessonState => {
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]

  if (!targetSlide || targetSlide.type !== 'question') return state

  const approveAllInSlide = (qSlide: QuestionSlideType): QuestionSlideType => ({
    ...qSlide,
    questionsByDifficulty: qSlide.questionsByDifficulty.map(diffGroup => ({
      ...diffGroup,
      questions: diffGroup.questions.map(q => ({ ...q, approved: true, isDisapproved: false }))
    })),
    allApproved: true
  })

  let newQuestionSlides: QuestionSlideType[]
  if (isAssessmentStage(targetSlide.learningStage)) {
    newQuestionSlides = state.questionSlides.map(qSlide =>
      isAssessmentStage(qSlide.learningStage) ? approveAllInSlide(qSlide) : qSlide
    )
  } else {
    newQuestionSlides = state.questionSlides.map(qSlide =>
      qSlide.learningStage === targetSlide.learningStage && qSlide.slide === targetSlide.slide
        ? approveAllInSlide(qSlide)
        : qSlide
    )
  }

  return {
    ...state,
    questionSlides: newQuestionSlides,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
    lastEditTimestamp: Date.now()
  }
}

const handleEditQuestion = (state: LessonState, action: { type: 'EDIT_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string; payload: Partial<Question> }): LessonState => {
  // Build slides array to find which questionSlide corresponds to slideIndex
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]

  if (!targetSlide || targetSlide.type !== 'question') return state

  // For assessment slides: update all Pre/Post by matching base ID
  if (isAssessmentStage(targetSlide.learningStage)) {
    // Find target question's base ID
    const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
    const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
    if (!targetQuestion) return state

    const baseId = getBaseQuestionId(targetQuestion.id)

    // Update all assessment questionSlides with matching base ID
    const newQuestionSlides = state.questionSlides.map(qSlide => {
      if (!isAssessmentStage(qSlide.learningStage)) return qSlide

      return {
        ...qSlide,
        questionsByDifficulty: qSlide.questionsByDifficulty.map(diffGroup => {
          if (diffGroup.difficulty !== action.difficulty) return diffGroup

          return {
            ...diffGroup,
            questions: diffGroup.questions.map(q => {
              if (getBaseQuestionId(q.id) === baseId) {
                const merged = mergeQuestion(q, action.payload)
                return { ...merged, approved: false, isDisapproved: false }
              }
              return q
            })
          }
        }),
        allApproved: false  // Editing unapproves the slide
      }
    })

    return {
      ...state,
      questionSlides: newQuestionSlides,
      totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
      lastEditTimestamp: Date.now()
    }
  }

  // For non-assessment: only update the matching questionSlide
  const newQuestionSlides = state.questionSlides.map(qSlide => {
    if (qSlide.learningStage !== targetSlide.learningStage || qSlide.slide !== targetSlide.slide) {
      return qSlide
    }

    const newQuestionsByDifficulty = updateQuestionsByDifficulty(
      qSlide.questionsByDifficulty,
      action.difficulty,
      action.questionIndex,
      q => {
        const merged = mergeQuestion(q, action.payload)
        return { ...merged, approved: false, isDisapproved: false }
      }
    )

    return {
      ...qSlide,
      questionsByDifficulty: newQuestionsByDifficulty,
      allApproved: false  // Editing unapproves the slide
    }
  })

  return {
    ...state,
    questionSlides: newQuestionSlides,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
    lastEditTimestamp: Date.now()  // Mark that an edit just happened
  }
}

const handleDeleteQuestion = (state: LessonState, action: { type: 'DELETE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }): LessonState => {
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]
  if (!targetSlide || targetSlide.type !== 'question') return state

  if (isAssessmentStage(targetSlide.learningStage)) {
    // Find the question's base ID to remove from all assessment slides
    const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
    const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
    if (!targetQuestion) return state
    const baseId = getBaseQuestionId(targetQuestion.id)

    const newQuestionSlides = state.questionSlides.map(qSlide => {
      if (!isAssessmentStage(qSlide.learningStage)) return qSlide
      const newQbd = qSlide.questionsByDifficulty.map(diffGroup => {
        if (diffGroup.difficulty !== action.difficulty) return diffGroup
        return {
          ...diffGroup,
          questions: diffGroup.questions.filter(q => getBaseQuestionId(q.id) !== baseId)
        }
      })
      return {
        ...qSlide,
        questionsByDifficulty: newQbd,
        allApproved: calculateSlideApproved(newQbd)
      }
    })

    const relabeled = recalculateDisplayLabels(newQuestionSlides)
    return {
      ...state,
      questionSlides: relabeled,
      totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(relabeled),
      lastEditTimestamp: Date.now()
    }
  }

  // Non-assessment: remove from the matching questionSlide
  const newQuestionSlides = state.questionSlides.map(qSlide => {
    if (qSlide.learningStage !== targetSlide.learningStage || qSlide.slide !== targetSlide.slide) return qSlide
    const newQbd = qSlide.questionsByDifficulty.map(diffGroup => {
      if (diffGroup.difficulty !== action.difficulty) return diffGroup
      return {
        ...diffGroup,
        questions: diffGroup.questions.filter((_, idx) => idx !== action.questionIndex)
      }
    })
    return {
      ...qSlide,
      questionsByDifficulty: newQbd,
      allApproved: calculateSlideApproved(newQbd)
    }
  })

  const relabeled = recalculateDisplayLabels(newQuestionSlides)
  return {
    ...state,
    questionSlides: relabeled,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(relabeled),
    lastEditTimestamp: Date.now()
  }
}

// Main reducer
const lessonReducer = (state: LessonState, action: LessonAction): LessonState => {
  switch (action.type) {
    case 'INIT_LESSON':
      return handleInitLesson(state, action)

    case 'APPROVE_QUESTION':
    case 'DISAPPROVE_QUESTION':
      return handleApproveQuestion(state, action)

    case 'APPROVE_ALL_QUESTIONS':
      return handleApproveAllQuestions(state, action)

    case 'EDIT_QUESTION':
      return handleEditQuestion(state, action)

    case 'DELETE_QUESTION':
      return handleDeleteQuestion(state, action)

    case 'SET_CURRENT_SLIDE':
      return {
        ...state,
        currentSlide: action.slideNum
      }

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        hideSidebar: !state.hideSidebar
      }

    default:
      return state
  }
}

const HideSideBarTooltip: React.FC = () => {
  return (
    <div className="hide-sidebar-tooltip">
      Hide sidebar
    </div>
  )
}

export const LessonBuilder: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search)
  const lessonId = searchParams.get('lessonId')
  const [showHideSidebarTooltip, setShowHideSidebarTooltip] = useState(false)
  const [showAssignClassOverlay, setShowAssignClassOverlay] = useState(false)
  const [showLaunchOverlay, setShowLaunchOverlay] = useState(false)
  const [showStudentLoginsOverlay, setShowStudentLoginsOverlay] = useState(false)
  const [copyOverlayMode, setCopyOverlayMode] = useState<'copy' | 'reassign' | null>(null)
  const [showPrintDraftOverlay, setShowPrintDraftOverlay] = useState(false)
  const [showPrintAnswerKeyOverlay, setShowPrintAnswerKeyOverlay] = useState(false)
  const [showPrintWorksheetsOverlay, setShowPrintWorksheetsOverlay] = useState(false)
  const [isClassAssigned, setIsClassAssigned] = useState(false)
  const [assignedClass, setAssignedClass] = useState<{ classId: string; name: string } | null>(null)
  const [showReadyPopup, setShowReadyPopup] = useState(false)
  const [modifiedQuestionIds, setModifiedQuestionIds] = useState<Set<string>>(new Set())
  const { data: assignedClassData } = useGetLessonAssignedClassQuery({ lessonId })
  const { data: hasResultsData } = useHasResultsQuery({ lessonId: lessonId! }, { skip: !lessonId })
  const { data: classData } = useGetClassQuery({ classId: assignedClass?.classId! }, { skip: !assignedClass?.classId })
  const [updateLessonQuestions] = useUpdateLessonQuestionsMutation()
  const [updateLessonStatus] = useUpdateLessonStatusMutation()
  const [editQuestionEduquery] = useEditQuestionEduqueryMutation()
  const [renameLesson] = useRenameLessonMutation()
  const [deleteQuestionMutation] = useDeleteQuestionMutation()
  const { data: lessonData } = useGetLessonQuery({ lessonId: lessonId! }, { skip: !lessonId })
  const [isRenaming, setIsRenaming] = useState(false)
  const [renamingValue, setRenamingValue] = useState('')
  const prevStatusRef = useRef<string>('')

  const [lessonState, baseDispatch] = useReducer(lessonReducer, {
    name: '',
    lessonPdf: null,
    numSlides: 0,
    questionSlides: [],
    totalUnapprovedQuestions: 0,
    currentSlide: 0,
    hideSidebar: false,
    status: '',
    lastEditTimestamp: null
  })

  // Wrap dispatch to track edited questions
  const dispatch = useCallback((action: LessonAction) => {
    if (action.type === 'EDIT_QUESTION') {
      // Find the question ID being edited to track it
      const slides = buildSlidesArray(lessonState.questionSlides, lessonState.numSlides)
      const targetSlide = slides[action.slideIndex]
      if (targetSlide && targetSlide.type === 'question') {
        const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
        const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
        if (targetQuestion) {
          markQuestionAsModified(targetQuestion.id)
        }
      }
    }
    baseDispatch(action)
  }, [lessonState.questionSlides, lessonState.numSlides])

  const {
    name,
    lessonPdf,
    currentSlide,
    hideSidebar
  } = lessonState

  // Build slides array for rendering (computed from questionSlides)
  const slides = buildSlidesArray(lessonState.questionSlides, lessonState.numSlides)

  const allApproved = lessonState.totalUnapprovedQuestions === 0

 const handleQuestionApproval = useCallback((questionIndex: number, difficulty: string, approved: boolean) => {
    dispatch({
      type: approved ? 'APPROVE_QUESTION' : 'DISAPPROVE_QUESTION',
      slideIndex: currentSlide,
      questionIndex,
      difficulty
    })
  }, [currentSlide])

  const handleApproveAll = useCallback(() => {
    dispatch({ type: 'APPROVE_ALL_QUESTIONS', slideIndex: currentSlide })
  }, [currentSlide])

  const canDeleteQuestions = lessonState.status === 'Draft' && !hasResultsData?.hasResults

  const handleDeleteQuestion = useCallback((questionIndex: number, difficulty: string, questionId: string) => {
    // Optimistically update local state
    dispatch({
      type: 'DELETE_QUESTION',
      slideIndex: currentSlide,
      questionIndex,
      difficulty,
    })
    // Delete from backend (EduQuery + DynamoDB)
    if (lessonId) {
      deleteQuestionMutation({
        lessonId,
        questionId,
        difficulty,
        slideIndex: currentSlide,
        questionIndex,
      })
    }
  }, [currentSlide, lessonId, deleteQuestionMutation])

  // Add actions for slide navigation and sidebar
  const handleSlideChange = useCallback((slideNum: number) => {
    dispatch({
      type: 'SET_CURRENT_SLIDE',
      slideNum
    })
  }, [])

  const handleToggleSidebar = useCallback(() => {
    dispatch({
      type: 'TOGGLE_SIDEBAR'
    })
  }, [])

  const markQuestionAsModified = useCallback((questionId: string) => {
    setModifiedQuestionIds(prev => new Set(prev).add(questionId))
  }, [])

  const handleLaunchLesson = () => {
    setShowLaunchOverlay(true);
  };


  const navigate = useNavigate()

  useEffect(() => {
    verifyLogin(navigate)
    if (lessonData) {
      dispatch({
        type: 'INIT_LESSON',
        payload: lessonData
      })
    }
    if (assignedClassData) {
      setAssignedClass(assignedClassData)
      setIsClassAssigned(true)
    }
  }, [lessonData, assignedClassData])

  // Save to DynamoDB immediately when an edit happens (when "Done" is clicked)
  useEffect(() => {
    console.log('🔍 Save useEffect fired:', {
      timestamp: lessonState.lastEditTimestamp,
      hasQuestions: lessonState.questionSlides.length > 0,
      hasLessonId: !!lessonId
    })
    // Only save if:
    // 1. Timestamp is not null (indicating a real edit, not initial load)
    // 2. Lesson has been initialized
    if (lessonState.lastEditTimestamp !== null && lessonState.questionSlides.length > 0 && lessonId) {
      console.log('✅ Saving edited questions to DynamoDB (triggered by Done click)')
      // err-10: fire-and-forget save — a failure silently lost the edits
      updateLessonQuestions({ lessonId, questions: lessonState.questionSlides }).unwrap()
        .catch(() => showToast("We couldn't save your latest edits. Please check your connection and try again."))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonState.lastEditTimestamp, lessonId, lessonState.questionSlides])
  // updateLessonQuestions is a stable RTK Query mutation - doesn't need dependency tracking
  // questionSlides intentionally included to catch INIT_LESSON race condition (commit fa5edd9)

  useEffect(() => {
    // Only run this effect if:
    // 1. Lesson has been initialized (questionSlides array is not empty)
    // 2. All questions are approved (totalUnapprovedQuestions === 0)
    console.log(lessonState)
    if (lessonState.questionSlides.length > 0 && lessonState.totalUnapprovedQuestions === 0) {
      console.log('updating lesson questions')

      // err-10: these saves were fire-and-forget and the "Ready" popup showed
      // even when every one of them failed — edits silently lost
      const saveAndMarkReady = async () => {
        try {
          // Save questionSlides directly (no conversion needed)
          await updateLessonQuestions({ lessonId, questions: lessonState.questionSlides }).unwrap()
          // Update lesson status to Ready in DB
          await updateLessonStatus({ lessonId, status: 'Ready' }).unwrap()
        } catch (e) {
          showToast("We couldn't save your lesson as Ready. Please check your connection and approve again.")
          return
        }

        // Show popup only if user approved questions this session (not on page load)
        if (lessonState.lastEditTimestamp !== null) {
          setShowReadyPopup(true)
        }

        // Update EduQuery - only send modified questions
        lessonState.questionSlides.forEach(qSlide => {
          qSlide.questionsByDifficulty.forEach(difficultyGroup => {
            difficultyGroup.questions.forEach(question => {
              if (modifiedQuestionIds.has(question.id)) {
                editQuestionEduquery({
                  lessonId,
                  questionId: question.id,
                  question
                }).unwrap().catch(() =>
                  showToast('One of your question edits failed to sync. Please re-open and re-save that question.'))
              }
            })
          })
        })

        // Clear tracking after sync
        setModifiedQuestionIds(new Set())
      }
      saveAndMarkReady()
    }
  }, [lessonState.totalUnapprovedQuestions])

  // When a question is disapproved and lesson was Ready, update status to Draft in DB + library
  useEffect(() => {
    const prevStatus = prevStatusRef.current
    prevStatusRef.current = lessonState.status

    if (prevStatus === 'Ready' && lessonState.status === 'Draft' && lessonId) {
      updateLessonStatus({ lessonId, status: 'Draft' })
    }
  }, [lessonState.status])

  const handleStartRename = () => {
    setRenamingValue(name)
    setIsRenaming(true)
  }

  const handleConfirmRename = async () => {
    if (renamingValue.trim() && lessonId) {
      try {
        await renameLesson({ lessonId, name: renamingValue.trim() })
        dispatch({ type: 'INIT_LESSON', payload: { ...lessonData, name: renamingValue.trim() } })
      } catch (error) {
        console.error('Error renaming lesson:', error)
      }
    }
    setIsRenaming(false)
  }

  const handleCancelRename = () => {
    setIsRenaming(false)
    setRenamingValue('')
  }

  useDocTitle(lessonState.name || 'All for One - Lesson Builder')

  return (
    <LessonProvider lessonId={lessonId}>
      <div className="lesson-builder">
        {showReadyPopup && (
          <div className="overlay">
            <div className={popupStyles.successCard}>
              <div className={popupStyles.successMessage}>
                Lesson is ready!
              </div>
              <div className={popupStyles.successActions}>
                <BlueButton label="Done" onClick={() => setShowReadyPopup(false)} />
              </div>
            </div>
          </div>
        )}
        {showAssignClassOverlay && (
          <AssignClassOverlay
            lessonId={lessonId}
            lessonGradeBand={lessonData?.gradeBand}
            onDismiss={() => setShowAssignClassOverlay(false)}
            setIsClassAssigned={setIsClassAssigned}
            setAssignedClass={setAssignedClass}
          />
        )}
        {showLaunchOverlay && (
          <LaunchLessonOverlay
            lessonName={name}
            lessonId={lessonId}
            className={assignedClass?.name}
            onDismiss={() => setShowLaunchOverlay(false)}
            onLaunch={() => {
              // Handle actual launch logic
              if (assignedClass?.classId && lessonId) {
                navigate(`/present/${assignedClass.classId}/${lessonId}`)
              }
            }}
            buttonTextA="Cancel"
            buttonTextB="Open"
          />
        )}
        {showStudentLoginsOverlay && classData && (
          <StudentLoginsOverlay
            classData={classData}
            onDismiss={() => setShowStudentLoginsOverlay(false)}
          />
        )}
        {copyOverlayMode && (
          <CopyToClassOverlay
            mode={copyOverlayMode}
            lessonId={lessonId!}
            lessonName={name}
            currentClassId={assignedClass?.classId || ''}
            currentGradeBand={lessonData?.gradeBand}
            onDismiss={() => setCopyOverlayMode(null)}
          />
        )}
        {showPrintDraftOverlay && (
          <PrintDraftOverlay
            lesson={lessonData!}
            className={assignedClass?.name}
            gradeBand={lessonData?.gradeBand}
            subject={lessonData?.subject}
            classData={classData}
            onDismiss={() => setShowPrintDraftOverlay(false)}
          />
        )}
        {showPrintAnswerKeyOverlay && (
          <PrintAnswerKeyOverlay
            lesson={lessonData!}
            className={assignedClass?.name}
            gradeBand={lessonData?.gradeBand}
            subject={lessonData?.subject}
            classData={classData}
            onDismiss={() => setShowPrintAnswerKeyOverlay(false)}
          />
        )}
        {showPrintWorksheetsOverlay && (
          <PrintWorksheetsOverlay
            lesson={lessonData!}
            assignedClassName={assignedClass?.name}
            gradeBand={lessonData?.gradeBand}
            subject={lessonData?.subject}
            classData={classData}
            onDismiss={() => setShowPrintWorksheetsOverlay(false)}
          />
        )}

        <LessonBuilderHeader
          allApproved={allApproved}
          unapprovedCount={lessonState.totalUnapprovedQuestions}
          teachStatus={lessonState.status}
          lessonName={name}
          setShowAssignClassOverlay={setShowAssignClassOverlay}
          isClassAssigned={isClassAssigned}
          assignedClassName={assignedClass?.name}
          gradeBand={lessonData?.gradeBand}
          hasResults={hasResultsData?.hasResults}
          onLaunchLesson={handleLaunchLesson}
          onShowStudentLogins={() => setShowStudentLoginsOverlay(true)}
          onCopyToClass={() => setCopyOverlayMode('copy')}
          onReassign={() => setCopyOverlayMode('reassign')}
          onPrintDraft={() => setShowPrintDraftOverlay(true)}
          onPrintAnswerKey={() => setShowPrintAnswerKeyOverlay(true)}
          onPrintWorksheets={() => setShowPrintWorksheetsOverlay(true)}
          isRenaming={isRenaming}
          renamingValue={renamingValue}
          onStartRename={handleStartRename}
          onRenamingValueChange={setRenamingValue}
          onConfirmRename={handleConfirmRename}
          onCancelRename={handleCancelRename}
        />
        <div
          className={'workspace ' + (hideSidebar ? 'hide-sidebar' : '')}
        >
          <div className="sidebar-container">
            <Sidebar
              lessonPdf={lessonPdf}
              slides={slides}
              currentSlide={currentSlide}
              hide={hideSidebar}
              onSlideClick={handleSlideChange}
            />
            <div
              className="toggle-sidebar"
              onClick={handleToggleSidebar}
              onMouseOver={() => setShowHideSidebarTooltip(true)}
              onMouseOut={() => setShowHideSidebarTooltip(false)}
            >
              <img src={sideBarIcon} alt="toggle sidebar" />
              {showHideSidebarTooltip && <HideSideBarTooltip />}
            </div>
          </div>
          <div className="slide-column">
            <div className="slide-area">
              {slides[currentSlide]?.type === 'presentation'
                ? (<PresentationSlide
                  lessonPdf={lessonPdf}
                  pageNumber={slides[currentSlide].index! + 1}
                  isCurrent={currentSlide === slides[currentSlide].index}
                />)
                : <QuestionSlide
                  slideIndex={currentSlide}
                  slideQuestions={slides[currentSlide]?.questionsByDifficulty}
                  learningStage={slides[currentSlide]?.learningStage}
                  onApproveQuestion={handleQuestionApproval}
                  onApproveAll={handleApproveAll}
                  dispatch={dispatch}
                  canDelete={canDeleteQuestions}
                  onDeleteQuestion={handleDeleteQuestion}
                  canEdit={!hasResultsData?.hasResults}
                />
              }
            </div>
            <SlideNav
              slides={slides}
              currentSlide={currentSlide}
              onChange={handleSlideChange}
              onToggleSidebar={handleToggleSidebar}
            />
          </div>
        </div>
      </div>
    </LessonProvider>
  )
}
